import classnames from "classnames";
import { ToolTips, TOGGLE_FILTERS_TOOLTIP, RESET_FILTERS_TOOLTIP } from "utils/hiddenToolTips";
import { LegacyMetricSelector } from "components/core/blocks/MetricSelector";
import { PaginationWidget } from "../../table/Pagination";
import { functionalUpdate, type Updater } from "@tanstack/react-table";
import type { ComponentProps } from "react";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { MdSearch, MdRotateLeft, MdFilterList } from "react-icons/md";

interface Props {
  className?: string;
  pages: number;
  page: number;
  pageSizeOptions: number[];
  pageSize: number;
  canPrevious: boolean;
  canNext: boolean;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
  style?: React.CSSProperties;
  disableSearchFocus?: boolean;
  data: unknown[];
  hideSearch?: boolean;
  hideFilters?: boolean;
  hideFilterToggleIcon?: boolean;
  hideResetFiltersIcon?: boolean;
  hideResultsCount?: boolean;
  updateSearch?: (value: string) => void;
  searchTerm: string;
  clearSearchTermFilters: () => void;
  filterWidget?: boolean;
  filterControlsWidth?: string | number;
  searchWidth?: string;
  additionalFilters?: React.ReactNode;
  additionalFilterRow?: React.ReactNode;
  children?: React.ReactNode;
  searchTagsInput?: React.ReactNode;
  singlePage?: boolean;
  showFilters: boolean;
  setShowFilters: (value: boolean) => void;
  columnSelectorProps?: Partial<ComponentProps<typeof LegacyMetricSelector>> & {
    onColumnChange: ComponentProps<typeof LegacyMetricSelector>["onMetricChange"];
    columnOptions: ComponentProps<typeof LegacyMetricSelector>["columnOptions"];
    clearSelectedColumns: () => void;
  };
  entityType?: string;
  groupMetrics?: boolean;
  setSavedColumnOrder: (args: Record<string, number>) => void;
  setSortState?: () => void;
  setFilterState?: (args: unknown) => void;
  clearFilters: () => void;
  isTop?: boolean;
  canSetPageSize?: boolean;
  overrideResultsCount?: number;
}

export default ({
  className,
  pages,
  page,
  pageSizeOptions,
  pageSize,
  canPrevious,
  canNext,
  onPageSizeChange,
  onPageChange,
  style,
  disableSearchFocus = false,
  data,
  hideSearch,
  hideFilters,
  hideFilterToggleIcon,
  hideResetFiltersIcon,
  updateSearch,
  searchTerm,
  clearSearchTermFilters,
  filterWidget,
  filterControlsWidth,
  searchWidth,
  additionalFilters,
  additionalFilterRow,
  children,
  searchTagsInput,
  singlePage,
  showFilters,
  setShowFilters,
  columnSelectorProps,
  entityType,
  groupMetrics,
  setSavedColumnOrder,
  setSortState,
  setFilterState,
  clearFilters,
  hideResultsCount,
  isTop,
  canSetPageSize,
  overrideResultsCount,
}: Props) => {
  if (!disableSearchFocus) {
    !hideSearch && document.querySelector<HTMLInputElement>(".searchInput")?.focus();
  }

  const getSafePage = (nextPage: number) => {
    if (Number.isNaN(nextPage)) {
      nextPage = page;
    }
    return Math.min(Math.max(nextPage, 0), pages - 1);
  };

  const changePage = (nextPage: Updater<number>) => {
    nextPage = functionalUpdate(nextPage, page);
    nextPage = getSafePage(nextPage);
    onPageChange(nextPage);
  };

  const resetFilters = () => {
    const table = document.querySelector(".ReactTable");
    const filterRow = table?.querySelector(".-filters");
    const dropdownFilters = filterRow?.querySelectorAll("select");
    const inputFilters = filterRow?.querySelectorAll<HTMLInputElement>("input[type=text]");

    updateSearch?.("");
    setSortState?.();

    if (inputFilters) {
      inputFilters.forEach((filter) => {
        filter.value = "";
      });
    }
    if (dropdownFilters) {
      dropdownFilters.forEach((filter) => {
        filter.selectedIndex = 0;
        filter.value = "all";
      });
    }
  };

  const toggleFilter = () => {
    setShowFilters(!showFilters);
  };

  const rowCount = data.length;

  return (
    <>
      <div className={classnames(className, "-pagination")} style={style}>
        <VStack gap="xsmall">
          <HStack
            style={{
              width: filterControlsWidth ? filterControlsWidth : "37.5%",
            }}
            alignSelf="start"
          >
            {isTop && !hideFilters && (
              <>
                {!hideFilterToggleIcon && (
                  <button
                    className="btn-toggle-filters"
                    style={{
                      background: "#fff",
                      color: "#000",
                      fontSize: "2rem",
                      padding: "1rem",
                      position: "relative",
                    }}
                    onClick={() => {
                      toggleFilter();
                    }}
                  >
                    <ToolTips toolTip={TOGGLE_FILTERS_TOOLTIP} position={"top"} />
                    <MdFilterList className="icon" style={{ fontSize: "24px", fontWeight: "bold" }} />
                  </button>
                )}
                {!hideResetFiltersIcon && (
                  <button
                    className="btn-reset-filters "
                    style={{
                      background: "#fff",
                      color: "#000",
                      fontSize: "2rem",
                      padding: "1rem",
                      position: "relative",
                    }}
                    onClick={() => {
                      resetFilters();
                      clearFilters();
                    }}
                  >
                    <ToolTips toolTip={RESET_FILTERS_TOOLTIP} position={"top"} />
                    <MdRotateLeft style={{ fontSize: "24px", fontWeight: "bold" }} />
                  </button>
                )}
                {columnSelectorProps && (
                  <LegacyMetricSelector
                    groupMetrics={groupMetrics}
                    selectorType="table"
                    entityType={entityType}
                    {...columnSelectorProps}
                    onMetricChange={columnSelectorProps.onColumnChange}
                    onResetMetrics={() => {
                      columnSelectorProps.clearSelectedColumns();
                      setSavedColumnOrder({});
                    }}
                    setSavedColumnOrder={(args) => {
                      setSavedColumnOrder(args);
                    }}
                  />
                )}
              </>
            )}

            {isTop && !hideSearch && (
              <>
                <div
                  style={{
                    position: "relative",
                    marginTop: "4px",
                    marginRight: "auto",
                    marginBottom: "auto",
                    marginLeft: "12px",
                  }}
                >
                  <MdSearch
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "8px",
                      fontSize: "2rem",
                    }}
                  />
                  <input
                    type="text"
                    className="searchInput"
                    style={{
                      textAlign: "left",
                      padding: "10px 10px 10px 35px",
                      width: searchWidth ? searchWidth : "400px",
                    }}
                    placeholder="Search..."
                    key={1224}
                    value={searchTerm}
                    ref={(input) => {
                      if (!disableSearchFocus && (input?.value.length ?? 0) > 0) {
                        input?.focus();
                      }
                    }}
                    onChange={(e) => {
                      updateSearch?.(e.target.value);
                      setFilterState?.([{ id: "product", value: e.target.value }]);

                      if (e.target.value.trim().length < 1) {
                        clearSearchTermFilters();
                      }
                    }}
                  ></input>
                  {searchTagsInput}
                </div>
                {children && !filterWidget && <div className="w-100 d-flex">{children}</div>}
              </>
            )}
          </HStack>
          {isTop && filterWidget && <div className="me-auto filter-widget-container">{children}</div>}
        </VStack>
        {isTop && additionalFilters && <div style={{ marginLeft: "auto" }}>{additionalFilters}</div>}

        {rowCount > 5 && !singlePage && (
          <PaginationWidget
            canPaginateBackward={canPrevious}
            currentPage={page}
            pageSize={pageSize}
            setCurrentPage={changePage}
            setPageSize={onPageSizeChange}
            canPaginateForward={canNext}
            pageSizeOptions={pageSizeOptions}
            canSetPageSize={canSetPageSize}
            totalPages={pages || 1}
            totalCount={overrideResultsCount ? overrideResultsCount : rowCount}
            hideResultsCount={hideResultsCount}
          />
        )}
      </div>
      {isTop && additionalFilterRow && (
        <div className={classnames(className, "-pagination")} style={style}>
          {additionalFilterRow}
        </div>
      )}
    </>
  );
};
